import React, {useEffect, useState} from 'react';
import styles from './styles.module.scss';
import Loader from "../../components/Loader";
import CompanyObserver from "../../mobx/company/CompanyObserver";
import FetchService from "../../services/api/FetchService";
import BaseLayout from '../../layouts/BaseLayout';
import {IBillingInfoResource} from "../../resources/IBilling.resource";
import {observer} from "mobx-react-lite";
import {formatDuration, formatNumber} from "../../utils/formatters";

type Tab = 'overview' | 'metrics';

const BillingPage = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<Tab>('overview');
    const [billingInfo, setBillingInfo] = useState<IBillingInfoResource>({
        totalCallsByDateCreate: 0,
        totalDurationByDateCreate: 0,
        totalCallsByCallTime: 0,
        totalDurationByCallTime: 0,
        totalCallsByCallTimeWithTimeZone: 0,
        totalDurationByCallTimeWithTimeZone: 0,
        checklistMetrics: [],
    });
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
    const [selectedTimeZone, setSelectedTimeZone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

    const loadBillingInfo = async () => {
        if (isLoading) return;

        const companyId = CompanyObserver.getCompanyId();
        if (!companyId) return;

        try {
            setIsLoading(true);
            const data = await FetchService.billingInfo(companyId, selectedYear, selectedMonth, selectedTimeZone);
            setBillingInfo(data.data);
        } finally {
            setIsLoading(false);
        }
    }

    const linkTelegramAccount = async () => {
        if (isLoading) return;

        const companyId = CompanyObserver.getCompanyId();
        if (!companyId) return;

        const queryParams = new URLSearchParams(window.location.search);
        const linkToken = queryParams.get('linkToken');
        if (linkToken) {
            try {
                setIsLoading(true);
                await FetchService.linkTelegramAccount(linkToken);
            } catch (e) {
                console.log('error:', e);
            } finally {
                setIsLoading(false);
            }
        }
    }

    useEffect(() => {
        loadBillingInfo();
    }, [CompanyObserver.companyId, selectedYear, selectedMonth, selectedTimeZone]);

    useEffect(() => {
        linkTelegramAccount();
    }, []);


    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedYear(Number(event.target.value));
    };

    const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedMonth(Number(event.target.value));
    };

    const handleTimeZoneChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedTimeZone(event.target.value);
    };

    const years = Array.from({length: 10}, (_, i) => new Date().getFullYear() - i);
    const months = Array.from({length: 12}, (_, i) => i + 1);
    const timeZones = [
        'UTC',
        'Europe/Kiev',
        'Europe/Bratislava',
        'Europe/Bucharest',
        'Europe/Budapest',
        'Europe/Madrid',
        'America/Mexico_City',
    ];
    if (!timeZones.includes(selectedTimeZone)) {
        timeZones.unshift(selectedTimeZone);
    }

    return (
        <>
            {isLoading ? <Loader/> : null}
            <BaseLayout title={''} withSidebar={false}>
                <div className={styles.billingWrapper}>
                    <div className={styles.billing}>
                        <div className={styles.filters}>
                            <select value={selectedYear} onChange={handleYearChange}>
                                {years.map(year => <option key={year} value={year}>{year}</option>)}
                            </select>
                            <select value={selectedMonth} onChange={handleMonthChange}>
                                {months.map(month => <option key={month} value={month}>{month}</option>)}
                            </select>
                            <select value={selectedTimeZone} onChange={handleTimeZoneChange}>
                                {timeZones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                            </select>
                        </div>

                        <div className={styles.tabs}>
                            <button
                                className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
                                onClick={() => setActiveTab('overview')}
                            >
                                Overview
                            </button>
                            <button
                                className={`${styles.tabButton} ${activeTab === 'metrics' ? styles.active : ''}`}
                                onClick={() => setActiveTab('metrics')}
                            >
                                Check-Lists
                            </button>
                        </div>

                        <div className={styles.tabContent}>
                            {activeTab === 'overview' && (
                                <div className={styles.info}>
                                    <div className={styles.group}>
                                        <h4>Official Billing Data (UTC)</h4>
                                        <div>Total Calls (Upload
                                            Time): <span>{formatNumber(billingInfo.totalCallsByDateCreate)}</span></div>
                                        <div>Total Duration (Upload
                                            Time): <span>{formatDuration(billingInfo.totalDurationByDateCreate)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.group}>
                                        <h4>Informational Data (for reference)</h4>
                                        <div>Total Calls (Call Time,
                                            UTC): <span>{formatNumber(billingInfo.totalCallsByCallTime)}</span></div>
                                        <div>Total Duration (Call Time,
                                            UTC): <span>{formatDuration(billingInfo.totalDurationByCallTime)}</span>
                                        </div>
                                        <div>Total Calls (Call Time, in selected Time
                                            Zone): <span>{formatNumber(billingInfo.totalCallsByCallTimeWithTimeZone)}</span>
                                        </div>
                                        <div>Total Duration (Call Time, in selected Time
                                            Zone): <span>{formatDuration(billingInfo.totalDurationByCallTimeWithTimeZone)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'metrics' && (
                                <>
                                    {billingInfo.checklistMetrics.length > 0 ? (
                                        <div className={styles.checklistGrid}>
                                            {billingInfo.checklistMetrics.map(metric => (
                                                <div key={metric.checklistId} className={styles.checklistItem}>
                                                    <h5>{metric.checklistName}</h5>
                                                    <div>Total Calls: <span>{formatNumber(metric.totalCalls)}</span>
                                                    </div>
                                                    <div>Total
                                                        Duration: <span>{formatDuration(metric.totalDuration)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.noData}>No checklist data for the selected period.</div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </BaseLayout>
        </>
    );
};

export default observer(BillingPage);
